import {ComponentStore} from '@ngrx/component-store';
import {Injectable, Injector} from '@angular/core';
import {DashboardWidget, DashboardWidgetConfig, DashboardWidgetConfigData, ReportDashboardWidgetState} from './report-dashboard';
import {forkJoin, Observable, of, Subscription, switchMap, withLatestFrom, combineLatest, Subject} from 'rxjs';
import {
  AdRComparison,
  AdReportParams,
  AdRequestOptions,
  AdRequestParams,
  AdRParamsFilters,
  AdRPeriod,
  AdRReport
} from '../../interfaces/ad-reports';
import {ReportStoreState} from './report';
import {Serializer} from '../../interfaces/serializer';
import {Site} from '../../interfaces/site';
import {filter, map, tap} from 'rxjs/operators';
import {ReportStore} from './report.store';
import {AppState} from '../store';
import {Store} from '@ngrx/store';
import {selectSite} from '../init/init.selectors';
import {WidgetTableComponent} from '../../../reports/dashboard-report/dashboard-report/widgets/widget-table/widget-table.component';
import * as lodash from 'lodash';
import {AdRRequestParams} from '../../classes/ad-r-request-params';
import {WidgetChartComponent} from '../../../reports/dashboard-report/dashboard-report/widgets/widget-chart/widget-chart.component';
import {WidgetTextComponent} from '../../../reports/dashboard-report/dashboard-report/widgets/widget-text/widget-text.component';
import {WidgetTotalsComponent} from '../../../reports/dashboard-report/dashboard-report/widgets/widget-totals/widget-totals.component';
import {ComponentType} from '@angular/cdk/overlay';
import {ReportsRequestsService} from '../../services/reports-requests.service';
import {Moment} from 'moment';
import {PeriodsService} from '../../services/periods.service';
import {Actions, ofType} from '@ngrx/effects';
import {reportDashboardUpdateWidget} from './report.actions';
import {TypedAction} from '@ngrx/store/src/models';
import {ReportDashboardStore} from './report-dashboard.store';
import {GridsterItem, GridsterItemComponentInterface} from 'angular-gridster2';

@Injectable()
export class ReportDashboardWidgetStore extends ComponentStore<ReportDashboardWidgetState> {
  private readonly _dataSet$: Observable<Serializer<Site>> = this._store.select(selectSite);

  public readonly loading$: Observable<boolean> = this.select((state: ReportDashboardWidgetState): boolean => state.loading);
  public readonly data$: Observable<AdRReport | Array<AdRReport>> = this.select((state: ReportDashboardWidgetState): AdRReport | Array<AdRReport> => state.data);
  public readonly componentType$: Observable<ComponentType<WidgetTextComponent | WidgetChartComponent | WidgetTableComponent | WidgetTotalsComponent>> = this.select((state: ReportDashboardWidgetState): ComponentType<WidgetTextComponent | WidgetChartComponent | WidgetTableComponent | WidgetTotalsComponent> => state.componentType);
  public readonly componentInjector$: Observable<Injector> = this.select((state: ReportDashboardWidgetState): Injector => state.componentInjector);

  public readonly from$: Observable<string | null> = this.select((state: ReportDashboardWidgetState): string | null => state.from);
  public readonly to$: Observable<string | null> = this.select((state: ReportDashboardWidgetState): string | null => state.to);
  public readonly fromComparison$: Observable<string | null> = this.select((state: ReportDashboardWidgetState): string | null => state.fromComparison);
  public readonly toComparison$: Observable<string | null> = this.select((state: ReportDashboardWidgetState): string | null => state.toComparison);

  private readonly _requestParamsChanges$: Observable<Partial<AdRequestParams> | Array<Partial<AdRequestParams>>> = this.select((state: ReportDashboardWidgetState): Partial<AdRequestParams> | Array<Partial<AdRequestParams>> | null => state.requestParams)
    .pipe(
      filter((params: Partial<AdRequestParams> | Array<Partial<AdRequestParams>> | null): boolean => !!params)
    );

  private readonly _load: () => void = this.effect((params$: Observable<void>): Observable<Array<AdRReport>> => params$.pipe(
    withLatestFrom(this._reportStore.state$, this.state$, this._dataSet$),
    tap((): void => {
      this.patchState({
        loading: true
      });
    }),
    // get data
    switchMap(([_, reportStoreState, state, dataSet]: [void, ReportStoreState, ReportDashboardWidgetState, Serializer<Site>]) => {
      let requests: Array<Observable<AdRReport>>;

      if (![WidgetTextComponent, WidgetTableComponent].includes(state.widget.config.widget as ComponentType<any>)) {
        if (Array.isArray(state.requestParams)) {
          requests = state.requestParams.map((params: Partial<AdRRequestParams>) => this._getData(
            dataSet,
            reportStoreState.uuid,
            reportStoreState.token,
            state.widget,
            params,
            reportStoreState.requestOptions
          ));
        } else {
          requests = [
            this._getData(
              dataSet,
              reportStoreState.uuid,
              reportStoreState.token,
              state.widget,
              state.requestParams,
              reportStoreState.requestOptions
            )
          ];
        }
      } else {
        requests = [
          of(null)
        ];
      }

      return forkJoin(requests).pipe(
        tap((reports: Array<AdRReport>): void => {
          const data: AdRReport | Array<AdRReport> = Array.isArray(state.requestParams) && reports || reports[0];

          this.patchState({
            loading: false,
            data: data,
            componentType: state.widget.config.widget as ComponentType<WidgetTextComponent | WidgetChartComponent | WidgetTableComponent | WidgetTotalsComponent>,
            componentInjector: Injector.create({
              parent: this._injector,
              providers: [
                { provide: 'widget', useValue: state.widget },
                { provide: 'widgetRequestParams', useValue: state.requestParams },
                { provide: 'widgetReportParams', useValue: state.reportParams },
                { provide: 'widgetRequestOptions', useValue: reportStoreState.requestOptions },
                { provide: 'widgetData', useValue: data },
                { provide: 'readonly', useValue: null },
                { provide: 'uuid', useValue: reportStoreState.uuid },
                { provide: 'token', useValue: reportStoreState.token },
                { provide: 'report', useValue: reportStoreState.report },
                { provide: 'template', useValue: reportStoreState.template },
              ]
            })
          });
        })
      );
    })
  ));

  private readonly _params$: Subscription = this.effect((): Observable<any> => this._requestParamsChanges$.pipe(
    tap((): void => this._load())
  ));

  private readonly _options$: Subscription = this.effect((): Observable<Partial<AdRequestOptions>> => this._reportStore.requestOptionsChanges$.pipe(
    tap((): void => this._load())
  ));

  private readonly _updateWidget$: Subscription = this.effect(() => this._actions$.pipe(
    ofType(reportDashboardUpdateWidget),
    withLatestFrom(this.state$),
    filter(([action, state]: [TypedAction<string> & { widget: DashboardWidget, config: Partial<DashboardWidgetConfig>, dashboard: Partial<DashboardWidget>[] }, ReportDashboardWidgetState]): boolean => action.widget === state.widget),
    tap(([action, state]: [TypedAction<string> & { widget: DashboardWidget, config: Partial<DashboardWidgetConfig>, params: Partial<AdReportParams>, dashboard: Partial<DashboardWidget>[] }, ReportDashboardWidgetState]): void => {
      action.widget.config = {
        ...action.widget.config,
        ...action.config
      };

      action.widget.params = action.params;

      this.patchState({
        widget: action.widget
      });

      this._initComponent();
      this._reportDashboardStore.updateWidgets(action.dashboard);
    })
  ));

  private readonly _initComponent: () => void = this.effect((params$: Observable<void>) => params$.pipe(
    withLatestFrom(this._reportStore.state$, this.state$),
    tap(([_, reportStoreState, state]: [void, ReportStoreState, ReportDashboardWidgetState]): void => {
      let requestParams: Partial<AdRequestParams> | Array<Partial<AdRequestParams>>;
      let reportParams: Partial<AdReportParams> | Array<Partial<AdReportParams>>;
      let from: string, to: string, fromComparison: string, toComparison: string;

      if (state.widget.config.data) {
        requestParams = state.widget.config.data.map((config: DashboardWidgetConfigData): Partial<AdRequestParams> => this._getParams(state.widget.config, config, reportStoreState));
        reportParams = state.widget.config.data.map((config: DashboardWidgetConfigData): Partial<AdReportParams> => config.params);
      } else {
        requestParams = this._getParams(state.widget.config, state.widget.config, reportStoreState);
        reportParams = state.widget.params;
      }

      const period: AdRPeriod = (requestParams?.[0] || requestParams).period;
      const comparison: AdRComparison | undefined = (requestParams?.[0] || requestParams).compare;

      // period
      const range: Array<Moment> = this._periodsService.getRange(period.type);
      if (period.type === 'custom') {
        from = period.from;
        to = period.to;
      } else {
        from = range[0].format('YYYY-MM-DD');
        to = range[1].format('YYYY-MM-DD');
      }

      if (comparison?.on) {
        const rangeCompare: Array<Moment> = this._periodsService.getRange(comparison.value.type);

        if (comparison.value.type === 'custom') {
          fromComparison = comparison.value.from;
          toComparison = comparison.value.to;
        } else {
          fromComparison = rangeCompare[0].format('YYYY-MM-DD');
          toComparison = rangeCompare[1].format('YYYY-MM-DD');
        }
      }


      this.patchState({
        requestParams: requestParams,
        reportParams: reportParams,
        from: from,
        to: to,
        fromComparison: fromComparison,
        toComparison: toComparison
      });
    })
  ));

  public readonly resize: (event: { item: GridsterItem, itemComponent: GridsterItemComponentInterface }) => void = this.effect((params$: Observable<{ item: GridsterItem, itemComponent: GridsterItemComponentInterface }>): Observable<[{ item: GridsterItem, itemComponent: GridsterItemComponentInterface }, ReportDashboardWidgetState]> => params$.pipe(
    withLatestFrom(this.state$),
    tap(([event, state]: [{ item: GridsterItem, itemComponent: GridsterItemComponentInterface }, ReportDashboardWidgetState]): void => {
      state.resizeFunction(event);
    })
  ));

  private readonly _init: (params: { widget: DashboardWidget }) => void = this.effect((params$: Observable<{ widget: DashboardWidget }>) => params$.pipe(
    switchMap((params: { widget: DashboardWidget }) => this._reportStore.reportParamsSubmitted$.pipe(
      tap((): void => {
        this.patchState({
          widget: params.widget
        });

        this._initComponent();
      }))
    )
  ));

  constructor(
    private readonly _store: Store<AppState>,
    private readonly _actions$: Actions,
    private readonly _reportStore: ReportStore,
    private readonly _reportDashboardStore: ReportDashboardStore,
    private readonly _injector: Injector,
    private readonly _reportsRequestsService: ReportsRequestsService,
    private readonly _periodsService: PeriodsService
  ) {
    super({
      widget: null,
      componentType: null,
      componentInjector: null,
      requestParams: null,
      reportParams: null,
      data: null,
      from: null,
      to: null,
      fromComparison: null,
      toComparison: null,
      resizeFunction: (event: { item: GridsterItem, itemComponent: GridsterItemComponentInterface }): void => {},
      loading: false
    });
  }

  public init(
    widget: DashboardWidget
  ): void {
    this._init({ widget: widget });
  }

  public setResizeFunction(resizeFunction: (event: { item: GridsterItem, itemComponent: GridsterItemComponentInterface }) => void): void {
    this.patchState({
      resizeFunction: resizeFunction
    });
  }

  public update(): void {}

  private _getFilters(dashboardRequestParams: Partial<AdRequestParams>, widgetRequestParams: Partial<AdRequestParams>): AdRParamsFilters {
    const filters: AdRParamsFilters = dashboardRequestParams.filters && lodash.cloneDeep(dashboardRequestParams.filters) || {};

    for (const key in widgetRequestParams.filters) {
      if (filters.hasOwnProperty(key)) {
        const values: Array<any> = filters[key];

        for (const value of widgetRequestParams.filters[key]) {
          if (!values.includes(value)) {
            values.push(value);
          }
        }

        filters[key] = values;
      } else {
        filters[key] = widgetRequestParams.filters[key];
      }
    }

    return filters;
  }

  private _getParams(config: Partial<DashboardWidgetConfig>, widgetRequestParams: Partial<AdRequestParams>, reportStoreState: ReportStoreState): Partial<AdRequestParams> {
    const params: Partial<AdRequestParams> = new AdRRequestParams();

    params.dimensions = widgetRequestParams.dimensions || [];
    params.metrics = widgetRequestParams.metrics || [];
    params.dimension_filters = widgetRequestParams.dimension_filters || [];
    params.dimension_orders = widgetRequestParams.dimension_orders || [];
    params.metric_filters = widgetRequestParams.metric_filters || [];
    params.metric_orders = widgetRequestParams.metric_orders || [];
    params.filters = this._getFilters(widgetRequestParams, reportStoreState.requestParams) || {};
    params.period = config.useDashboardPeriod && reportStoreState.requestParams.period || config.period || { type: 'last_30_days' };
    params.column_state = config.column_state || null;
    params.report_id = reportStoreState.report?.id || null;
    params.template_id = reportStoreState.template?.id || null;

    if (config.widget !== WidgetChartComponent) {
      params.compare = config.useDashboardPeriod && reportStoreState.requestParams.compare || config.compare || { on: null, value: null };
    } else {
      params.compare = { on: null, value: null };
    }

    params.page = 1;
    params.limit = -1;

    if (['day', 'month', 'week', 'year'].includes(params.dimensions[0])) {
      params.dimension_orders = [{sort: 'desc', slug: params.dimensions[0], action: 'value'}];
    }

    return params;
  }

  private _getData(
    dataSet: Serializer<Site>,
    uuid: string | null,
    token: string | null,
    widget: DashboardWidget,
    requestParams: Partial<AdRRequestParams>,
    requestOptions: Partial<AdRequestOptions>
  ): Observable<AdRReport> {
    return forkJoin([
      // report
      this._reportsRequestsService.generate(
        dataSet,
        uuid,
        token,
        requestParams,
        {
          ...requestOptions,
          ...(widget.config.widget === WidgetTotalsComponent && { round_big_numbers: true }
            || widget.config.widget === WidgetChartComponent && { generate_missing_date_chart_data: true }
            || {})
        }
      ),
      widget.config.widget === WidgetTotalsComponent &&
      requestParams.compare?.on &&
      this._reportsRequestsService.generate(
        dataSet,
        uuid,
        token,
        {
          ...requestParams,
          dimensions: ['channel'],
          dimension_orders: []
        },
        {
          ...requestOptions,
          ...(widget.config.widget === WidgetTotalsComponent && { round_big_numbers: true }
            || widget.config.widget === WidgetChartComponent && { generate_missing_date_chart_data: true }
            || {})
        }
      ) || of(null)
    ]).pipe(
      map(([report, comparison]: [AdRReport, AdRReport]) => {
        if (comparison) {
          report.totals = comparison.totals;
        }

        return report;
      })
    );
  }

}
